/**
 * Quotation rules (design H) as pure functions: the deposit asked for on
 * acceptance and what is left of it, the customer-response copy for the list
 * and the detail card, the validity hint, and which actions each quotation
 * state offers. The server still decides; these only choose what to show.
 */
import type { Discount, InvoiceDoc, InvoiceListItem, ListQuery } from '../../internals/repository/InvoiceRepository';
import { ago, dateTime, daysBetween, money, todayColombo } from './format';
import { divRound } from './math/invoiceMath';

export type QuoteTab = 'ALL' | 'DRAFT' | 'SENT' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED' | 'INVOICED';

export const QUOTE_TABS: { key: QuoteTab; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'DRAFT', label: 'Draft' },
  { key: 'SENT', label: 'Sent' },
  { key: 'ACCEPTED', label: 'Accepted' },
  { key: 'DECLINED', label: 'Declined' },
  { key: 'EXPIRED', label: 'Expired' },
  { key: 'INVOICED', label: 'Invoiced' },
];

export function quoteListQuery(tab: QuoteTab, q = ''): ListQuery {
  const query: ListQuery = { docType: 'QUOTATION', tab };
  if (q.trim()) query.q = q.trim();
  return query;
}

/** A quotation as the API returns it, plus the fields set once it is invoiced. */
export type QuoteDoc = Partial<InvoiceDoc> & {
  displayStatus: string;
  invoicedDocId?: string;
  invoicedNumber?: string;
};

export const isQuoteId = (id?: string | null) => /^quo_/.test(id || '');

// ── Deposit ────────────────────────────────────────────────────────────────

/** PERCENT (basis points) → total × bp / 10000 half up; AMOUNT → at most the total. */
export function depositAmount(req: Discount | null | undefined, totalMinor: number): number {
  if (!req || !(req.value > 0) || !(totalMinor > 0)) return 0;
  if (req.type === 'PERCENT') return divRound(totalMinor * Math.min(req.value, 10000), 10000);
  return Math.min(req.value, totalMinor);
}

/** 2500 → "25%", 1250 → "12.5%". */
export const percentText = (bp: number) => `${String(Math.floor(bp / 100))}${bp % 100 ? `.${String(bp % 100).padStart(2, '0').replace(/0$/, '')}` : ''}%`;

/** "25% = Rs. 26,250.00", or just the amount for a fixed deposit. */
export function depositLabel(req: Discount | null | undefined, totalMinor: number, currency = 'LKR'): string {
  if (!req) return '';
  const amt = money(depositAmount(req, totalMinor), currency);
  return req.type === 'PERCENT' ? `${percentText(req.value)} = ${amt}` : amt;
}

export interface DepositView { requestedMinor: number; paidMinor: number; remainingMinor: number; label: string }

/** Null when the quotation asks for no deposit. */
export function depositView(doc: Pick<QuoteDoc, 'depositRequest' | 'totalMinor' | 'paidMinor'>, currency = 'LKR'): DepositView | null {
  const requested = depositAmount(doc.depositRequest, doc.totalMinor || 0);
  if (!requested) return null;
  const paid = Math.max(0, doc.paidMinor || 0);
  return {
    requestedMinor: requested,
    paidMinor: paid,
    remainingMinor: Math.max(0, requested - paid),
    label: depositLabel(doc.depositRequest, doc.totalMinor || 0, currency),
  };
}

// ── Customer response ──────────────────────────────────────────────────────

type ResponseLike = Pick<InvoiceListItem, 'displayStatus' | 'viewedAt' | 'quoteResponse'>;

/** The list's response column. */
export function responseText(it: ResponseLike): string {
  const r = it.quoteResponse;
  if (r?.kind) {
    const word = r.kind === 'ACCEPTED' ? 'Accepted' : 'Declined';
    return r.note?.trim() ? `${word} · ${r.note.trim()}` : word;
  }
  if (it.viewedAt && (it.displayStatus === 'Q_SENT' || it.displayStatus === 'Q_EXPIRED')) return 'Viewed, no reply yet';
  return '—';
}

export interface ResponseCard { tone: 'ok' | 'danger' | 'neutral'; title: string; note?: string; when: string }

export function responseCard(doc: Pick<QuoteDoc, 'displayStatus' | 'viewedAt' | 'quoteResponse'>, now = Date.now()): ResponseCard {
  const r = doc.quoteResponse;
  if (r?.kind) {
    return {
      tone: r.kind === 'ACCEPTED' ? 'ok' : 'danger',
      title: r.kind === 'ACCEPTED' ? 'Accepted by the customer' : 'Declined by the customer',
      note: r.note?.trim() || undefined,
      when: r.at ? dateTime(r.at) : '',
    };
  }
  switch (doc.displayStatus) {
    case 'Q_DRAFT': return { tone: 'neutral', title: 'Not sent yet', when: 'The customer can reply once it is issued.' };
    case 'Q_SENT':
      return { tone: 'neutral', title: 'Waiting for the customer', when: doc.viewedAt ? `Viewed ${ago(doc.viewedAt, now)}` : 'Sent, not viewed yet' };
    case 'Q_EXPIRED': return { tone: 'neutral', title: 'Expired without a reply', when: doc.viewedAt ? `Viewed ${ago(doc.viewedAt, now)}` : '' };
    default: return { tone: 'neutral', title: 'No reply recorded', when: '' };
  }
}

// ── Validity ───────────────────────────────────────────────────────────────

/** Red "Expired" or "In N days" for a quotation still waiting for a reply. */
export function expiryHint(validUntil: string | undefined, displayStatus: string, today = todayColombo()): { text: string; expired: boolean } | null {
  if (displayStatus === 'Q_EXPIRED') return { text: 'Expired', expired: true };
  if (displayStatus !== 'Q_SENT' || !validUntil) return null;
  const d = daysBetween(today, validUntil);
  if (d < 0) return { text: 'Expired', expired: true };
  if (d === 0) return { text: 'Expires today', expired: false };
  return { text: `In ${d} day${d === 1 ? '' : 's'}`, expired: false };
}

// ── Actions ────────────────────────────────────────────────────────────────

export type QuoteAction = 'edit' | 'send' | 'share' | 'duplicate' | 'convert' | 'openInvoice' | 'recordDeposit' | 'void';

/**
 * Convert: the server takes SENT or ACCEPTED (not yet invoiced). Void: an
 * issued quotation with no deposit paid (a paid deposit blocks the void).
 * Record deposit: accepted, with part of the requested deposit unpaid.
 */
export function quoteActions(doc: Pick<QuoteDoc, 'displayStatus'> & Partial<Pick<QuoteDoc, 'paidMinor' | 'totalMinor' | 'depositRequest' | 'invoicedDocId'>>): Set<QuoteAction> {
  const s = doc.displayStatus;
  const a = new Set<QuoteAction>(['duplicate']);
  if (s === 'Q_DRAFT') a.add('edit');
  if (s === 'Q_SENT' || s === 'Q_ACCEPTED') {
    a.add('send'); a.add('share'); a.add('convert');
    if (!((doc.paidMinor || 0) > 0)) a.add('void');
  }
  if (s === 'Q_ACCEPTED' && (depositView(doc)?.remainingMinor ?? 0) > 0) a.add('recordDeposit');
  if (s === 'Q_INVOICED' && doc.invoicedDocId) a.add('openInvoice');
  return a;
}

// ── Converted invoice ──────────────────────────────────────────────────────

/** Editor banner for an invoice made from a quotation. */
export function originText(quote: Pick<QuoteDoc, 'number' | 'depositRequest' | 'totalMinor' | 'paidMinor'>, currency = 'LKR'): string {
  const parts = [`From quotation ${quote.number ?? ''}`.trim()];
  const paid = quote.paidMinor || 0;
  if (paid > 0) parts.push(`deposit of ${money(paid, currency)} paid`);
  else if (quote.depositRequest) {
    const req = quote.depositRequest;
    parts.push(`deposit of ${req.type === 'PERCENT' ? percentText(req.value) : money(req.value, currency)} requested`);
  }
  return parts.join(' · ');
}
